import { api } from './authService';
import { conditionHasColumn, conditionMergeKey } from '../components/segmentBuilder/criteriaUtils';

const BASE = '/api/v2/offer_reports/studio';

export const BASELINE_OPTIONS = [
  { id: 'universe', label: 'Universe', short: 'universe' },
  { id: 'clickers', label: 'Offer clickers', short: 'clickers' },
  { id: 'vertical', label: 'Vertical converters', short: 'vertical' },
];

/** Short badge label naming which of the four report flavors is shown, mirroring
 * `detailsTypeLabel` in OfferReport.js but reading the flavor the backend computes. */
export function flavorLabel(offer) {
  const mode = offer?.flavor;
  if (mode === 'values') return 'Lite';
  if (mode === 'vertical') return `Vertical — ${offer?.category || 'Uncategorized'}`;
  if (mode === 'general') return 'General';
  return 'Full Report';
}

export const LAST_OFFER_KEY = 'cube.offerReports.lastOffer';

export function readLastOffer() {
  try {
    return window.localStorage.getItem(LAST_OFFER_KEY) || '';
  } catch {
    return '';
  }
}

export function writeLastOffer(offerId) {
  try {
    if (offerId) window.localStorage.setItem(LAST_OFFER_KEY, String(offerId));
  } catch {
    /* ignore */
  }
}

export function emptyAudience() {
  return {
    version: 2,
    groupsOp: 'OR',
    activeGroupId: 'g1',
    groups: [{ id: 'g1', name: '', rules: [] }],
    globalExcludes: [],
  };
}

export function isAudience(value) {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.groups));
}

export function audienceFromLegacyRules(rules) {
  const list = Array.isArray(rules) ? rules : [];
  return {
    version: 2,
    groupsOp: 'OR',
    activeGroupId: 'g1',
    // Includes and excludes both live on the group. The exclude tray is only
    // for rules that must apply to every cohort, which a single group does not need.
    groups: [{ id: 'g1', name: '', rules: list }],
    globalExcludes: [],
  };
}

export function normalizeAudience(value) {
  if (isAudience(value)) {
    const groups = (value.groups || []).map((g, i) => ({
      id: String(g?.id || `g${i + 1}`),
      name: String(g?.name || ''),
      rules: Array.isArray(g?.rules) ? g.rules : [],
    }));
    const safe = groups.length ? groups : emptyAudience().groups;
    const active = safe.some((g) => g.id === value.activeGroupId) ? value.activeGroupId : safe[0].id;
    return {
      version: 2,
      groupsOp: value.groupsOp === 'AND' ? 'AND' : 'OR',
      activeGroupId: active,
      groups: safe,
      globalExcludes: Array.isArray(value.globalExcludes) ? value.globalExcludes : [],
    };
  }
  if (Array.isArray(value)) return audienceFromLegacyRules(value);
  return emptyAudience();
}

export function audienceRuleCount(audience) {
  const a = normalizeAudience(audience);
  return a.groups.reduce((n, g) => n + g.rules.length, 0) + a.globalExcludes.length;
}

export function allAudienceRules(audience) {
  const a = normalizeAudience(audience);
  return [...a.groups.flatMap((g) => g.rules), ...a.globalExcludes];
}

export function activeGroup(audience) {
  const a = normalizeAudience(audience);
  return a.groups.find((g) => g.id === a.activeGroupId) || a.groups[0];
}

/** Encode explore rules for the URL (`?rules=`) and the segment deep link. */
export function encodeRules(rulesOrAudience) {
  const a = normalizeAudience(rulesOrAudience);
  if (!audienceRuleCount(a)) return '';
  // One unnamed group stays a flat array so existing bookmarks and the editor
  // deep link keep working. Two or more groups need the v2 object.
  const payload =
    a.groups.length === 1 && !a.groups[0].name ? [...a.groups[0].rules, ...a.globalExcludes] : a;
  const json = JSON.stringify(payload);
  const b64 = window.btoa(unescape(encodeURIComponent(json)));
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeRules(encoded) {
  if (!encoded) return emptyAudience();
  try {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '==='.slice((b64.length + 3) % 4);
    const json = decodeURIComponent(escape(window.atob(padded)));
    const parsed = JSON.parse(json);
    return normalizeAudience(parsed);
  } catch {
    return emptyAudience();
  }
}

/** Build a segment-builder condition from a (feature, value) finding. */
/**
 * `value.segment_condition` (from the API) carries the exact profile column, its type,
 * operator and translated value(s) when the feature maps to one; otherwise the rule
 * keeps the label + original value and the editor asks for a column. The type matters
 * beyond display: without it the column reads as a string, which allows neither
 * `between` nor any other numeric or date operator the mapping may have chosen.
 */
export function ruleFromFinding(feature, value) {
  const sc = value.segment_condition || null;
  const under = value.index != null && value.index < 100;
  const stored = under ? value.exclude_rule : value.include_rule;
  const label = under ? value.exclude_label : value.include_label;
  if (stored && typeof stored === 'object') {
    const stamped = {
      ...stored,
      offer_feature: stored.offer_feature || feature.feature,
      offer_value: stored.offer_value ?? value.value,
      offer_value_label: stored.offer_value_label || value.value_label,
      offer_direction: under ? 'under' : 'over',
    };
    if (stamped.kind === 'group' && label && !stamped.display_name) {
      stamped.display_name = label;
    } else if (stamped.kind !== 'group' && label && !stamped.display_name) {
      stamped.display_name = label;
    }
    return stamped;
  }
  return {
    kind: 'condition',
    catalog: sc?.catalog || '',
    schema: sc?.schema || '',
    table: sc?.table || '',
    column: sc?.column || '',
    column_type: sc?.column_type || '',
    has_value_list: Boolean(sc?.has_value_list),
    display_name: feature.display_label,
    operator: sc?.operator || (under ? 'neq' : 'eq'),
    value: sc ? sc.value : value.value,
    value2: sc?.value2 || '',
    folder_list: [],
    offer_feature: feature.feature,
    offer_value: value.value,
    offer_value_label: value.value_label,
    offer_direction: under ? 'under' : 'over',
  };
}

export const RULE_OP_SYMBOL = {
  eq: '=',
  neq: '≠',
  in: 'is any of',
  not_in: 'is not any of',
  between: 'between',
  is_null: 'is empty',
  is_not_null: 'is not empty',
};

export function ruleKey(rule) {
  return `${rule.offer_feature || rule.column}::${rule.offer_value ?? rule.value}`;
}

/** Where a finding currently lives: the active cohort, another cohort, the exclude tray, or nowhere. */
export function rulePresence(audience, feature, value) {
  const a = normalizeAudience(audience);
  const key = `${feature}::${value}`;
  if (a.globalExcludes.some((r) => ruleKey(r) === key)) return 'exclude';
  for (const g of a.groups) {
    if (g.rules.some((r) => ruleKey(r) === key)) {
      return g.id === a.activeGroupId ? 'active' : 'elsewhere';
    }
  }
  return null;
}

export function toggleAudienceRule(audience, rule) {
  const a = normalizeAudience(audience);
  const key = ruleKey(rule);
  const targetId = activeGroup(a).id;
  const inActive = a.groups.some((g) => g.id === targetId && g.rules.some((r) => ruleKey(r) === key));
  const inTray = a.globalExcludes.some((r) => ruleKey(r) === key);
  if (inActive) {
    return {
      ...a,
      groups: a.groups.map((g) =>
        g.id === targetId ? { ...g, rules: g.rules.filter((r) => ruleKey(r) !== key) } : g
      ),
    };
  }
  if (inTray) {
    return { ...a, globalExcludes: a.globalExcludes.filter((r) => ruleKey(r) !== key) };
  }
  return {
    ...a,
    globalExcludes: a.globalExcludes.filter((r) => ruleKey(r) !== key),
    groups: a.groups.map((g) => (g.id === targetId ? { ...g, rules: [...g.rules, rule] } : g)),
  };
}

export function removeAudienceRule(audience, rule) {
  const a = normalizeAudience(audience);
  const key = ruleKey(rule);
  return {
    ...a,
    groups: a.groups.map((g) => ({ ...g, rules: g.rules.filter((r) => ruleKey(r) !== key) })),
    globalExcludes: a.globalExcludes.filter((r) => ruleKey(r) !== key),
  };
}

export function startAudienceGroup(audience, groupsOp) {
  const a = normalizeAudience(audience);
  const current = activeGroup(a);
  if (!current?.rules.length) return a;
  const used = new Set(a.groups.map((g) => g.id));
  let n = a.groups.length + 1;
  let id = `g${n}`;
  while (used.has(id)) {
    n += 1;
    id = `g${n}`;
  }
  // A leftover exclude tray belonged to the cohort we just left — keep it there
  // so the new group does not inherit "exclude from everyone".
  const groups = a.groups.map((g) =>
    g.id === current.id && a.globalExcludes.length ? { ...g, rules: [...g.rules, ...a.globalExcludes] } : g
  );
  return {
    ...a,
    groupsOp: groupsOp === 'AND' || groupsOp === 'OR' ? groupsOp : 'OR',
    activeGroupId: id,
    globalExcludes: [],
    groups: [...groups, { id, name: '', rules: [] }],
  };
}

export function setActiveAudienceGroup(audience, groupId) {
  const a = normalizeAudience(audience);
  if (!a.groups.some((g) => g.id === groupId)) return a;
  return { ...a, activeGroupId: groupId };
}

export function removeAudienceGroup(audience, groupId) {
  const a = normalizeAudience(audience);
  if (a.groups.length <= 1) return a;
  const idx = a.groups.findIndex((g) => g.id === groupId);
  if (idx < 0) return a;
  const groups = a.groups.filter((g) => g.id !== groupId);
  const neighbor = a.groups[idx - 1] || a.groups[idx + 1];
  const activeGroupId = a.activeGroupId === groupId ? neighbor.id : a.activeGroupId;
  // Starting a second group flips the studio to ANY. Removing the last extra
  // group is the undo of that, so a single cohort is ALL again.
  return {
    ...a,
    groups,
    activeGroupId,
    groupsOp: groups.length === 1 ? 'AND' : a.groupsOp,
  };
}

export function renameAudienceGroup(audience, groupId, name) {
  const a = normalizeAudience(audience);
  return {
    ...a,
    groups: a.groups.map((g) => (g.id === groupId ? { ...g, name: String(name || '') } : g)),
  };
}

export function setAudienceGroupsOp(audience, groupsOp) {
  return { ...normalizeAudience(audience), groupsOp: groupsOp === 'AND' ? 'AND' : 'OR' };
}

/**
 * Display names of the attributes whose rules have no profile column, in the order they
 * were picked and each named once. An offer-metric feature only maps to a column where
 * `FEATURE_SEGMENT_COLUMN` says so, and a rule without one cannot be counted or turned
 * into SQL — the editor asks for the column instead.
 */
export function attributesNeedingColumn(rules) {
  const needsColumn = (rule) => {
    if (!rule) return false;
    if (rule.kind === 'group') return (rule.children || []).some(needsColumn);
    return !conditionHasColumn(rule);
  };
  const names = [];
  (Array.isArray(rules) ? rules : []).forEach((rule) => {
    if (!needsColumn(rule)) return;
    const name = rule?.display_name || rule?.offer_feature || 'an attribute';
    if (!names.includes(name)) names.push(name);
  });
  return names;
}

/** How many attributes to name before the rest become a count. */
const MAX_NAMED_ATTRIBUTES = 3;

/** Why the size is missing, naming the attributes holding it up. */
export function needsColumnNote(names) {
  const list = Array.isArray(names) ? names : [];
  if (!list.length) return '';
  const shown = list.slice(0, MAX_NAMED_ATTRIBUTES).map((n) => `“${n}”`);
  const rest = list.length - shown.length;
  if (rest > 0) shown.push(`${rest} more`);
  const named =
    shown.length === 1 ? shown[0] : `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  const verb = list.length === 1 ? 'has' : 'have';
  const pick = list.length === 1 ? 'Pick one' : 'Pick one for each';
  return `${named} ${verb} no profile column yet. ${pick} in the segment editor to size this.`;
}

/** Operators that no longer describe a single value once a second pick joins them. */
const MERGED_OP = { eq: 'in', in: 'in', neq: 'not_in', not_in: 'not_in' };

/** True when a rule's operator reads on its own, with no value to show. */
export function ruleNeedsNoValue(operator) {
  return operator === 'is_null' || operator === 'is_not_null';
}

/**
 * Rules as the panel lists them: picks landing on one column become a single row,
 * because that is how they reach the segment — "gender is any of Female, Male", not two
 * conditions no profile can satisfy at once. Rows are keyed by the same function the
 * criteria builder groups on, so the list and the segment cannot drift apart.
 */
export function groupedRules(rules) {
  const list = Array.isArray(rules) ? rules : [];
  const rows = [];
  const byMergeKey = new Map();
  list.forEach((rule) => {
    const mergeKey = rule?.kind === 'group' ? '' : conditionMergeKey(rule);
    const row = mergeKey ? byMergeKey.get(mergeKey) : null;
    if (row) {
      row.rules.push(rule);
      row.operator = MERGED_OP[row.operator] || row.operator;
      return;
    }
    const next = { key: ruleKey(rule), rule, operator: rule?.operator, rules: [rule] };
    rows.push(next);
    if (mergeKey) byMergeKey.set(mergeKey, next);
  });
  return rows;
}

export const offerStudioService = {
  async listOffers(organizationId) {
    const res = await api.get(`${BASE}/offers`, { params: { organization_id: organizationId } });
    return res.data || [];
  },
  async overview(offerId, organizationId, baseline) {
    const res = await api.get(`${BASE}/${offerId}/overview`, {
      params: { organization_id: organizationId, baseline },
    });
    return res.data;
  },
  async features(offerId, organizationId, baseline, category) {
    const res = await api.get(`${BASE}/${offerId}/features`, {
      params: { organization_id: organizationId, baseline, category: category || undefined },
    });
    return res.data || [];
  },
  async insights(offerId, organizationId, baseline, limit = 12) {
    const res = await api.get(`${BASE}/${offerId}/insights`, {
      params: { organization_id: organizationId, baseline, limit },
    });
    return res.data || [];
  },
  async segmentSuggestions(offerId, organizationId, baseline, limit = 10) {
    const res = await api.get(`${BASE}/${offerId}/segment-suggestions`, {
      params: { organization_id: organizationId, baseline, limit },
    });
    return res.data || [];
  },
};
